import React, { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CityChip } from "@/components/customer/CityChip";
import { CustomerBottomNav } from "@/components/customer/CustomerBottomNav";
import { AIAssistant } from "@/components/customer/AIAssistant";
import { CategoryDrawer } from "@/components/customer/CategoryDrawer";

/** Layout for /customer/* — clean, centered brand header + shared bottom tabs.
 * Sky rebrand v2: solid white strip, thin bottom border, original raster
 * `/logo.png` (per user request). Admin + merchant surfaces render their own
 * shell and keep the shared `components/shared/Logo.jsx`.
 */
export function CustomerLayout({ children }) {
    const { user, logout } = useAuth();
    const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
    const districtHref = user?.district_slug ? `/home?district_slug=${user.district_slug}` : "/home";
    return (
        <div className="min-h-screen bg-white pb-24 lg:pb-6" data-brand="sky" data-testid="customer-shell">
            <header
                className="sticky top-0 z-30 border-b border-slate-200 bg-white"
                data-testid="customer-header"
            >
                <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-7 md:px-8 md:py-10">
                    {/* Left slot — hamburger opens Category Drawer */}
                    <div className="flex items-center">
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
                            aria-label="Browse categories"
                            data-testid="home-hamburger"
                            onClick={() => setCategoryDrawerOpen(true)}
                        >
                            <Menu size={22} className="text-neutral-700" />
                        </button>
                    </div>
                    {/* Centered brand logo — transparent-bg raster */}
                    <Link
                        to={districtHref}
                        className="flex items-center justify-center"
                        data-testid="customer-nav-logo"
                    >
                        <img
                            src="/logo.png"
                            alt="OfferNest"
                            className="h-24 w-auto object-contain md:h-32"
                        />
                    </Link>
                    {/* Right slot — city chip + quick logout */}
                    <div className="flex items-center gap-2 justify-end">
                        <CityChip />
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={logout}
                            className="h-9 w-9 text-[var(--text-muted)] hover:text-rose-600"
                            data-testid="customer-logout"
                        >
                            <LogOut size={16} />
                        </Button>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-6xl px-4 pb-8 pt-6 lg:px-8">{children || <Outlet />}</main>
            <CustomerBottomNav homeHref={districtHref} />
            <AIAssistant />
            <CategoryDrawer open={categoryDrawerOpen} onOpenChange={setCategoryDrawerOpen} />
        </div>
    );
}

export default CustomerLayout;
